import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Salad, Save, Trash2, Plus, Pencil, BookOpen, UtensilsCrossed, X, Coffee, Sun, Cookie, Moon } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";

const MEALS = [
  { key: "colazione", label: "Colazione", icon: Coffee, color: "text-rose-700" },
  { key: "pranzo", label: "Pranzo", icon: Sun, color: "text-amber-700" },
  { key: "merenda", label: "Merenda", icon: Cookie, color: "text-emerald-700" },
  { key: "cena", label: "Cena", icon: Moon, color: "text-indigo-700" },
];
const emptyMenu = { id: null, nome: "", colazione: "", pranzo: "", merenda: "", cena: "" };

export default function MenuPasti() {
  const { selectedStructure, structures } = useAuth();
  const qc = useQueryClient();
  const structId = selectedStructure !== "all" ? selectedStructure : ((structures || []).length === 1 ? structures[0].id : null);

  const [edit, setEdit] = useState(emptyMenu);
  const [dish, setDish] = useState({ testo: "", ricetta: "" });
  const [openRecipe, setOpenRecipe] = useState({});

  const { data: modelli = [] } = useQuery({
    queryKey: ["menu-modelli", structId], enabled: !!structId, staleTime: 0,
    queryFn: async () => (await api.get("/menu/modelli", { params: { structure_id: structId } })).data,
  });
  const { data: pasti = [] } = useQuery({
    queryKey: ["menu-ric", structId], enabled: !!structId, staleTime: 0,
    queryFn: async () => (await api.get("/menu/ricorrenti", { params: { structure_id: structId } })).data,
  });

  const saveMenu = async () => {
    if (!edit.nome.trim()) { toast.error("Dai un nome al menù"); return; }
    try {
      await api.post("/menu/modelli", { structure_id: structId, id: edit.id || undefined, nome: edit.nome.trim(),
        colazione: edit.colazione, pranzo: edit.pranzo, merenda: edit.merenda, cena: edit.cena });
      qc.invalidateQueries({ queryKey: ["menu-modelli"] });
      toast.success(edit.id ? "Menù aggiornato" : "Menù creato");
      setEdit(emptyMenu);
    } catch { toast.error("Errore nel salvataggio"); }
  };
  const delMenu = async (id) => {
    try { await api.delete(`/menu/modelli/${id}`); qc.invalidateQueries({ queryKey: ["menu-modelli"] }); if (edit.id === id) setEdit(emptyMenu); }
    catch { toast.error("Errore"); }
  };

  const saveDish = async () => {
    const t = dish.testo.trim(); if (!t) { toast.error("Scrivi il nome del pasto"); return; }
    try { await api.post("/menu/ricorrenti", { structure_id: structId, testo: t, ricetta: dish.ricetta }); qc.invalidateQueries({ queryKey: ["menu-ric"] }); setDish({ testo: "", ricetta: "" }); toast.success("Pasto salvato"); }
    catch { toast.error("Errore"); }
  };
  const saveRecipe = async (p, ricetta) => {
    try { await api.post("/menu/ricorrenti", { structure_id: structId, testo: p.testo, ricetta }); qc.invalidateQueries({ queryKey: ["menu-ric"] }); toast.success("Ricetta salvata"); }
    catch { toast.error("Errore"); }
  };
  const delDish = async (id) => { try { await api.delete(`/menu/ricorrenti/${id}`); qc.invalidateQueries({ queryKey: ["menu-ric"] }); } catch { toast.error("Errore"); } };

  const insertDish = (mealKey, testo) => {
    if (!testo) return;
    setEdit((p) => ({ ...p, [mealKey]: p[mealKey] ? `${p[mealKey].replace(/\n+$/, "")}\n${testo}` : testo }));
  };

  if (!structId) {
    return (
      <div>
        <h1 className="text-2xl font-head font-bold flex items-center gap-2 mb-3"><Salad size={22} className="text-orange-600" /> Menu & Pasti</h1>
        <div className="gr-card p-8 text-center" data-testid="menupasti-no-struct">
          <Salad size={34} className="mx-auto text-orange-400 mb-2" />
          <p className="text-sm text-muted-foreground">Seleziona una <b>struttura</b> dal selettore in alto per gestire menù e pasti.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-2xl font-head font-bold flex items-center gap-2"><Salad size={22} className="text-orange-600" /> Menu & Pasti</h1>
        <p className="text-sm text-muted-foreground">Crea menù con un nome da riutilizzare in Cucina e, se serve, imposta la ricetta dei pasti.</p>
      </div>

      <Tabs defaultValue="menu">
        <TabsList data-testid="menupasti-tabs">
          <TabsTrigger value="menu" data-testid="tab-menu-salvati"><UtensilsCrossed size={15} className="mr-1.5" /> Menu salvati</TabsTrigger>
          <TabsTrigger value="pasti" data-testid="tab-pasti-ricette"><BookOpen size={15} className="mr-1.5" /> Pasti e ricette</TabsTrigger>
        </TabsList>

        {/* MENU SALVATI */}
        <TabsContent value="menu" className="mt-4">
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="gr-card p-4" data-testid="menu-editor">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-head font-bold">{edit.id ? "Modifica menù" : "Nuovo menù"}</h3>
                {edit.id && <Button size="sm" variant="ghost" onClick={() => setEdit(emptyMenu)} data-testid="menu-editor-new">Nuovo</Button>}
              </div>
              <label className="text-xs font-semibold mb-1 block">Nome del menù</label>
              <Input value={edit.nome} onChange={(e) => setEdit((p) => ({ ...p, nome: e.target.value }))} placeholder="Es. Menù estivo — Lunedì" data-testid="menu-editor-nome" />
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                {MEALS.map((m) => (
                  <div key={m.key}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className={`flex items-center gap-1.5 text-sm font-semibold ${m.color}`}><m.icon size={15} /> {m.label}</div>
                      {pasti.length > 0 && (
                        <select defaultValue="" onChange={(e) => { if (e.target.value) { insertDish(m.key, e.target.value); e.target.value = ""; } }} className="h-8 border rounded-md px-2 text-xs max-w-[55%]" data-testid={`menu-editor-pick-${m.key}`} title="Scegli un pasto salvato">
                          <option value="">+ Pasto salvato…</option>
                          {pasti.map((p) => <option key={p.id} value={p.testo}>{p.testo}</option>)}
                        </select>
                      )}
                    </div>
                    <Textarea rows={4} value={edit[m.key]} onChange={(e) => setEdit((p) => ({ ...p, [m.key]: e.target.value }))} placeholder="Una voce per riga (es. Primo: pasta al pomodoro)" data-testid={`menu-editor-${m.key}`} />
                  </div>
                ))}
              </div>
              <div className="mt-3 flex justify-end">
                <Button onClick={saveMenu} data-testid="menu-editor-save"><Save size={15} className="mr-1.5" /> {edit.id ? "Aggiorna menù" : "Crea menù"}</Button>
              </div>
            </div>

            <div className="gr-card p-4" data-testid="menu-list">
              <h3 className="font-head font-bold mb-3">Menù salvati ({modelli.length})</h3>
              {modelli.length === 0 ? (
                <p className="text-sm text-muted-foreground italic" data-testid="menu-list-empty">Nessun menù salvato. Creane uno a sinistra: potrai poi selezionarlo in Cucina.</p>
              ) : (
                <div className="space-y-2">
                  {modelli.map((md) => (
                    <div key={md.id} className="rounded-lg border p-3 flex items-start justify-between gap-2" data-testid={`menu-item-${md.id}`}>
                      <div className="min-w-0">
                        <div className="font-semibold text-sm">{md.nome}</div>
                        <div className="text-xs text-muted-foreground mt-0.5 space-y-0.5">
                          {MEALS.map((m) => (md[m.key] || "").trim() && <div key={m.key} className="truncate"><span className={`font-semibold ${m.color}`}>{m.label}:</span> {(md[m.key] || "").replace(/\n/g, " · ")}</div>)}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setEdit({ id: md.id, nome: md.nome, colazione: md.colazione || "", pranzo: md.pranzo || "", merenda: md.merenda || "", cena: md.cena || "" })} data-testid={`menu-item-edit-${md.id}`}><Pencil size={15} /></Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-muted-foreground hover:text-rose-600" onClick={() => delMenu(md.id)} data-testid={`menu-item-del-${md.id}`}><Trash2 size={15} /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* PASTI E RICETTE */}
        <TabsContent value="pasti" className="mt-4">
          <div className="gr-card p-4 mb-4" data-testid="dish-add">
            <h3 className="font-head font-bold mb-3">Aggiungi un pasto</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold mb-1 block">Nome del pasto</label>
                <Input value={dish.testo} onChange={(e) => setDish((p) => ({ ...p, testo: e.target.value }))} placeholder="Es. Pasta al pomodoro" data-testid="dish-nome" />
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 block">Ricetta <span className="text-muted-foreground font-normal">(facoltativa)</span></label>
                <Textarea rows={2} value={dish.ricetta} onChange={(e) => setDish((p) => ({ ...p, ricetta: e.target.value }))} placeholder="Ingredienti e preparazione…" data-testid="dish-ricetta" />
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <Button onClick={saveDish} data-testid="dish-save"><Plus size={15} className="mr-1.5" /> Salva pasto</Button>
            </div>
          </div>

          <div className="gr-card p-4" data-testid="dish-list">
            <h3 className="font-head font-bold mb-3">Pasti salvati ({pasti.length})</h3>
            {pasti.length === 0 ? (
              <p className="text-sm text-muted-foreground italic" data-testid="dish-list-empty">Nessun pasto salvato. I pasti aggiunti qui vengono suggeriti in automatico mentre scrivi il menù in Cucina.</p>
            ) : (
              <div className="space-y-2">
                {pasti.map((p) => (
                  <div key={p.id} className="rounded-lg border p-3" data-testid={`dish-item-${p.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <button onClick={() => setOpenRecipe((s) => ({ ...s, [p.id]: !s[p.id] }))} className="font-semibold text-sm text-left hover:underline flex items-center gap-1.5" data-testid={`dish-toggle-${p.id}`}>
                        <BookOpen size={14} className="text-orange-600" /> {p.testo}
                        {p.ricetta ? <span className="text-[11px] font-normal text-emerald-700">· ricetta</span> : <span className="text-[11px] font-normal text-muted-foreground">· nessuna ricetta</span>}
                      </button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-muted-foreground hover:text-rose-600" onClick={() => delDish(p.id)} data-testid={`dish-del-${p.id}`}><Trash2 size={15} /></Button>
                    </div>
                    {openRecipe[p.id] && (
                      <div className="mt-2 pt-2 border-t" data-testid={`dish-recipe-${p.id}`}>
                        <Textarea rows={3} defaultValue={p.ricetta || ""} placeholder="Scrivi qui la ricetta…" id={`ric-${p.id}`} data-testid={`dish-recipe-input-${p.id}`} />
                        <div className="mt-2 flex justify-end">
                          <Button size="sm" onClick={() => saveRecipe(p, document.getElementById(`ric-${p.id}`).value)} data-testid={`dish-recipe-save-${p.id}`}><Save size={14} className="mr-1.5" /> Salva ricetta</Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
